import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CircleHelp, FileBadge } from "lucide-react";
import type { Driver } from "driver.js";
import { useTranslation } from "react-i18next";
import { adminApi, apiErrorMessage } from "@/lib/api";
import { showAdminToast } from "@/lib/admin-toast";
import { adminLicensePath } from "@/lib/navigation";
import { can, requiredRole } from "@/lib/permissions";
import {
  useCurrentIdentityQuery,
  useCustomersQuery,
  useProductsQuery,
} from "@/lib/queries";
import { useTimezone } from "@/lib/timezone";
import { parseJsonObject } from "@/lib/utils";
import {
  createLicenseSetupTour,
  LICENSE_SETUP_TOUR_ID,
  restoreFocusAfterTour,
} from "@/lib/license-setup-tour";
import type { CreateLicenseRequest } from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Select } from "@/components/ui/select";
import { BusyLabel, InlineAlert } from "@/components/admin/feedback";
import { LicenseConfigurationFields } from "@/components/admin/license-configuration-fields";
import { configurationDraftIsValid } from "@/components/admin/configuration-validation";
import { PageFrame } from "@/components/admin/page-frame";
import { PageHeader } from "@/components/admin/page-header";
import type { LicenseConfigurationValidationError } from "@/components/admin/license-configuration-fields";
import {
  LicenseCreationNavigation,
  LicenseCreationProgress,
  LicenseCreationStep,
} from "@/components/admin/license-creation-wizard";
import { PermissionHint } from "@/components/admin/status";
import {
  getValidityError,
  validityErrorMessage,
  type ValidityError,
} from "@/lib/license-validity";

export function CreateLicensePage() {
  const { t } = useTranslation();
  const { timezoneAbbr, formatDateTime } = useTimezone();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tourRef = useRef<Driver | null>(null);
  const tourTriggerRef = useRef<HTMLButtonElement | null>(null);
  const startTourRef = useRef<() => void>(() => {});
  const transitionFramesRef = useRef<number[]>([]);
  const identityQuery = useCurrentIdentityQuery();
  const customersQuery = useCustomersQuery();
  const productsQuery = useProductsQuery();
  const allowed = can(identityQuery.data?.roles, "createRecords");
  const [customerID, setCustomerID] = useState("");
  const [productID, setProductID] = useState("");
  const [alias, setAlias] = useState("");
  const [model, setModel] =
    useState<NonNullable<CreateLicenseRequest["model"]>>("node_locked");
  const [kind, setKind] = useState<"paid" | "trial">("paid");
  const [durationDays, setDurationDays] = useState("14");
  const [validFrom, setValidFrom] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [entitlements, setEntitlements] = useState("{}");
  const [policy, setPolicy] = useState('{"max_devices":1}');
  const [reason, setReason] = useState("");
  const [wizardStep, setWizardStep] = useState(0);
  const [isTourActive, setIsTourActive] = useState(false);
  const [limitsValidationError, setLimitsValidationError] = useState<LicenseConfigurationValidationError | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<"customer" | "product" | "reason", boolean>>({ customer: false, product: false, reason: false });
  const [validUntilInvalid, setValidUntilInvalid] = useState(false);
  const [trialDurationInvalid, setTrialDurationInvalid] = useState(false);
  const [validityError, setValidityError] = useState<ValidityError | null>(null);

  const cancelPendingTransitionFrames = useCallback(() => {
    transitionFramesRef.current.forEach((frame) => window.cancelAnimationFrame(frame));
    transitionFramesRef.current = [];
  }, []);

  useEffect(() => {
    return () => {
      cancelPendingTransitionFrames();
      const tour = tourRef.current;
      if (tour) {
        tourRef.current = null;
        tour.destroy();
      }
    };
  }, [cancelPendingTransitionFrames]);
  const validityMessage = validityError && validityErrorMessage(t, validityError);
  const isTrial = kind === "trial";
  const expiryRequirement = isTrial
    ? t("licenses.trialExpiryDerived", "derived from server UTC when issued")
    : model === "time_limited"
    ? t("licenses.expiryRequired", "required for Time limited")
    : model === "perpetual"
      ? t("licenses.expiryForbidden", "not applicable for Perpetual")
      : t("licenses.expiryOptional", "optional for Node locked");
  const expiryPlaceholder = isTrial
    ? t("licenses.trialExpiryPlaceholder", "Derived from Trial duration")
    : model === "time_limited"
    ? t("licenses.expiryPlaceholderRequired", "Set expiry (required)")
    : model === "perpetual"
      ? t("licenses.expiryPlaceholderForbidden", "No expiry for Perpetual")
      : t("licenses.expiryPlaceholderOptional", "Set expiry (optional)");
  const selectedProduct = productsQuery.data?.products.find(
    (product) => product.id === productID,
  );
  const selectedCustomer = customersQuery.data?.customers.find(
    (customer) => customer.id === customerID,
  );
  const trialEvidence = grantEvidence(entitlements, policy, t);
  const mutation = useMutation({
    mutationFn: (body: CreateLicenseRequest) => adminApi.createLicense(body),
    onSuccess: async (license) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin", "search"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "license-index"] }),
      ]);
      showAdminToast(t("search.details.actionRecorded", "Action recorded"));
      navigate(adminLicensePath(license.id));
    },
    onError: (value) => setError(apiErrorMessage(value)),
  });

  const clearTourRequest = useCallback(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("tour");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const TOUR_STEP_TO_WIZARD_STEP = [0, 2, 3, 3, 4] as const;

  const startTour = useCallback(() => {
    setWizardStep(0);
    setIsTourActive(true);

    if (tourRef.current) {
      const previous = tourRef.current;
      tourRef.current = null;
      previous.destroy();
    }

    let tour: Driver;
    tour = createLicenseSetupTour({
      t,
      canIssue: allowed,
      onNextClick: (_element, _step, { driver }) => {
        const active = driver.getActiveIndex();
        if (active === undefined) return;
        if (!driver.hasNextStep()) {
          driver.destroy();
          return;
        }
        const nextTourIndex = active + 1;
        const targetWizardStep = TOUR_STEP_TO_WIZARD_STEP[nextTourIndex] ?? 0;
        setWizardStep(targetWizardStep);
        cancelPendingTransitionFrames();
        const outerFrame = window.requestAnimationFrame(() => {
          const innerFrame = window.requestAnimationFrame(() => {
            driver.moveNext();
          });
          transitionFramesRef.current.push(innerFrame);
        });
        transitionFramesRef.current.push(outerFrame);
      },
      onPrevClick: (_element, _step, { driver }) => {
        const active = driver.getActiveIndex();
        if (active === undefined) return;
        if (!driver.hasPreviousStep()) return;
        const prevTourIndex = active - 1;
        const targetWizardStep = TOUR_STEP_TO_WIZARD_STEP[prevTourIndex] ?? 0;
        setWizardStep(targetWizardStep);
        cancelPendingTransitionFrames();
        const outerFrame = window.requestAnimationFrame(() => {
          const innerFrame = window.requestAnimationFrame(() => {
            driver.movePrevious();
          });
          transitionFramesRef.current.push(innerFrame);
        });
        transitionFramesRef.current.push(outerFrame);
      },
      onDestroyed: () => {
        if (tourRef.current !== tour) return;
        tourRef.current = null;
        cancelPendingTransitionFrames();
        setIsTourActive(false);
        setWizardStep(0);
        clearTourRequest();
        // Read the ref at destroy time: base-ui commits portal content after
        // the mount effects, so the tour button may not exist when a deep-
        // linked tour starts synchronously.
        restoreFocusAfterTour(tourTriggerRef.current);
      },
    });
    tourRef.current = tour;
    tour.drive();
  }, [allowed, clearTourRequest, t]);
  useEffect(() => {
    startTourRef.current = startTour;
  }, [startTour]);

  const tourRequested = searchParams.get("tour") === LICENSE_SETUP_TOUR_ID;
  useEffect(() => {
    if (!tourRequested) return;
    const startFrame = window.requestAnimationFrame(() => startTourRef.current());
    return () => {
      window.cancelAnimationFrame(startFrame);
      const tour = tourRef.current;
      tourRef.current = null;
      tour?.destroy();
      setIsTourActive(false);
    };
  }, [tourRequested]);

  function clearValidityError() {
    setValidUntilInvalid(false);
    setValidityError(null);
  }

  function moveToWizardStep(step: number, focusId?: string) {
    setWizardStep(step);
    requestAnimationFrame(() => {
      const target = focusId ? document.getElementById(focusId) : document.getElementById(`license-creation-step-${step}`);
      target?.focus();
    });
  }

  function reportValidityError(nextValidityError: ValidityError) {
    setValidUntilInvalid(true);
    setValidityError(nextValidityError);
    moveToWizardStep(2, "license-valid-until");
  }

  function validateAccessStep() {
    const duration = Number(durationDays);
    if (isTrial && (!Number.isInteger(duration) || duration < 1 || duration > 30)) {
      setTrialDurationInvalid(true);
      setError(t("licenses.trialDurationValidation", "Trial duration must be a whole number from 1 to 30 days."));
      moveToWizardStep(2, "license-trial-duration");
      return false;
    }
    const nextValidityError = isTrial
      ? null
      : getValidityError({ model, validFrom, validUntil, rejectPastExpiry: true });
    if (nextValidityError) {
      reportValidityError(nextValidityError);
      return false;
    }
    setTrialDurationInvalid(false);
    clearValidityError();
    return true;
  }

  function validateLimitsStep() {
    if (!configurationDraftIsValid()) return false;
    try {
      parseJsonObject(entitlements);
    } catch (value) {
      setLimitsValidationError("entitlements_json");
      setError(value instanceof Error ? value.message : t("licenses.jsonValidationError", "Entitlements and policy must be valid JSON objects."));
      moveToWizardStep(3);
      return false;
    }
    let policyObject: Record<string, unknown>;
    try {
      policyObject = parseJsonObject(policy);
      const maxDevices = effectiveMaxDevices(policyObject.max_devices);
      if (typeof maxDevices !== "number" || !Number.isInteger(maxDevices) || maxDevices < 1) {
        setLimitsValidationError("max_devices");
        setError(t("licenses.maxDevicesValidationError", "Max devices must be a positive whole number."));
        moveToWizardStep(3);
        return false;
      }
      setLimitsValidationError(null);
      return true;
    } catch (value) {
      setLimitsValidationError("policy_json");
      setError(value instanceof Error ? value.message : t("licenses.jsonValidationError", "Entitlements and policy must be valid JSON objects."));
      moveToWizardStep(3);
      return false;
    }
  }

  function advanceWizard() {
    if (wizardStep === 0 && !customerID.trim()) {
      setFieldErrors((current) => ({ ...current, customer: true }));
      setError(t("uxWizard.selectionRequired", { field: t("licenses.customer", "Customer"), defaultValue: "Select {{field}} before continuing." }));
      moveToWizardStep(0, "license-customer");
      return;
    }
    if (wizardStep === 1 && !productID.trim()) {
      setFieldErrors((current) => ({ ...current, product: true }));
      setError(t("uxWizard.selectionRequired", { field: t("licenses.product", "Product"), defaultValue: "Select {{field}} before continuing." }));
      moveToWizardStep(1, "license-product");
      return;
    }
    if (wizardStep === 2 && !validateAccessStep()) return;
    if (wizardStep === 3 && !validateLimitsStep()) return;
    setError(null);
    setWizardStep((current) => Math.min(current + 1, 4));
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) return;
    if (wizardStep !== 4) {
      advanceWizard();
      return;
    }
    const missing = { customer: !customerID.trim(), product: !productID.trim(), reason: !reason.trim() };
    if (missing.customer || missing.product || missing.reason) {
      setFieldErrors(missing);
      setError(t("licenses.requiredFieldsError", "Customer, Product, and reason are required."));
      if (missing.customer) moveToWizardStep(0, "license-customer");
      else if (missing.product) moveToWizardStep(1, "license-product");
      else moveToWizardStep(4, "license-reason");
      return;
    }
    if (!validateAccessStep() || !validateLimitsStep()) return;
    try {
      const entitlementObject = parseJsonObject(entitlements);
      const policyObject = parseJsonObject(policy);
      const duration = Number(durationDays);
      setError(null);
      setTrialDurationInvalid(false);
      setFieldErrors({ customer: false, product: false, reason: false });
      clearValidityError();
      const common = {
        customer_id: customerID.trim(),
        product_id: productID.trim(),
        kind,
        ...(alias.trim() ? { alias: alias.trim() } : {}),
        ...(!isTrial && validFrom ? { valid_from: validFrom } : {}),
        entitlements: entitlementObject as CreateLicenseRequest["entitlements"],
        policy: policyObject,
        reason: reason.trim(),
      };
      if (isTrial) {
        mutation.mutate({
          ...common,
          kind: "trial",
          model: "time_limited",
          duration_days: duration,
        } as CreateLicenseRequest);
      } else if (model === "time_limited") {
        mutation.mutate({ ...common, kind: "paid", model, valid_until: validUntil });
      } else if (model === "perpetual") {
        mutation.mutate({ ...common, kind: "paid", model, valid_until: null });
      } else {
        mutation.mutate({
          ...common,
          kind: "paid",
          model,
          ...(validUntil ? { valid_until: validUntil } : {}),
        });
      }
    } catch (value) {
      setError(
        value instanceof Error
          ? value.message
          : t("licenses.jsonValidationError", "Entitlements and policy must be valid JSON objects."),
      );
    }
  }

  return (
    <PageFrame size="fluid">
      <PageHeader
        title={
          <>
            <FileBadge className="size-6 text-muted sm:size-7" aria-hidden="true" />
            {t("licenses.createTitle", "Issue license")}
          </>
        }
        titleClassName="flex items-center gap-2.5"
        description={t("licenses.createDesc", "Configure commercial validity, Entitlements, device policy, and assign to a Customer.")}
        actions={
          <>
            <Button ref={tourTriggerRef} type="button" variant="ghost" size="sm" onClick={startTour}>
              <CircleHelp className="size-4" aria-hidden="true" />
              {t("help.startGuidedTour", "Start guided tour")}
            </Button>
            <Button variant="ghost" onClick={() => navigate("/admin/licenses")}>
              <ArrowLeft className="size-4" aria-hidden="true" />
              {t("common.back", "Back to licenses")}
            </Button>
          </>
        }
      />
      {!allowed && (
        <PermissionHint
          role={requiredRole("createRecords")}
          action="create licenses"
        />
      )}
      {wizardStep !== 4 && (error ?? validityMessage) && (
        <InlineAlert
          title={t("licenses.createErrorTitle", "License was not created")}
          message={error ?? validityMessage!}
          variant="error"
        />
      )}
      <form
        className="rounded-lg border border-line bg-panel p-5 sm:p-7"
        onSubmit={submit}
      >
        <fieldset disabled={!allowed} className="min-w-0 space-y-6">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-semibold text-ink">{t("search.details.licenseDetail", "License details")}</h2>
            <Badge variant="info">{t("common.reasonRequired", "Reason required")}</Badge>
          </div>
          <LicenseCreationProgress
            currentStep={wizardStep}
            onStepChange={setWizardStep}
            disabled={mutation.isPending}
          />
          <LicenseCreationStep id="license-creation-step-0" active={wizardStep === 0}>
            <div className="space-y-4" data-tour-id="license-context">
              <div>
                <h3 className="text-sm font-medium text-ink">{t("uxWizard.customerTitle", "Who receives this License?")}</h3>
                <p className="mt-1 text-xs leading-5 text-muted">{t("uxWizard.customerDescription", "Choose the organization that owns this commercial grant.")}</p>
              </div>
              <div className="max-w-md space-y-2">
                <Label htmlFor="license-customer">{t("licenses.customer", "Customer")}</Label>
                <Select
                  id="license-customer"
                  value={customerID}
                  onChange={(event) => { setCustomerID(event.target.value); setFieldErrors((current) => ({ ...current, customer: false })); }}
                  disabled={customersQuery.isPending}
                  aria-invalid={fieldErrors.customer}
                >
                  <option value="">{t("licenses.selectCustomer", "Select a customer")}</option>
                  {customersQuery.data?.customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} · {customer.id}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="max-w-md space-y-2">
                <Label htmlFor="license-alias">{t("licenses.alias", "Alias")} <span className="font-normal text-muted">({t("licenses.aliasOptional", "Optional display name for console lists")})</span></Label>
                <Input
                  id="license-alias"
                  value={alias}
                  onChange={(event) => setAlias(event.target.value)}
                  placeholder={t("licenses.aliasPlaceholder", "e.g. Design team 2026")}
                  maxLength={80}
                />
                <p className="text-xs text-muted">{t("licenses.aliasHelp", "Shown in License lists instead of the License ID. Maximum 80 characters. Admin metadata only; never included in Signed License Tokens.")}</p>
              </div>
              <LicenseCreationNavigation currentStep={0} onBack={() => {}} onNext={advanceWizard} disabled={mutation.isPending} />
            </div>
          </LicenseCreationStep>
          <LicenseCreationStep id="license-creation-step-1" active={wizardStep === 1}>
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-ink">{t("uxWizard.productTitle", "Which Product is covered?")}</h3>
                <p className="mt-1 text-xs leading-5 text-muted">{t("uxWizard.productDescription", "Its feature catalog defines the Entitlements available in the next step.")}</p>
              </div>
              <div className="max-w-md space-y-2">
                <Label htmlFor="license-product">{t("licenses.product", "Product")}</Label>
                <Select
                  id="license-product"
                  value={productID}
                  onChange={(event) => { setProductID(event.target.value); setFieldErrors((current) => ({ ...current, product: false })); }}
                  disabled={productsQuery.isPending}
                  aria-invalid={fieldErrors.product}
                >
                  <option value="">{t("licenses.selectProduct", "Select a product")}</option>
                  {productsQuery.data?.products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name} · {product.id}
                    </option>
                  ))}
                </Select>
              </div>
              <LicenseCreationNavigation currentStep={1} onBack={() => setWizardStep(0)} onNext={advanceWizard} disabled={mutation.isPending} />
            </div>
          </LicenseCreationStep>
          <LicenseCreationStep id="license-creation-step-2" active={wizardStep === 2}>
            <div className="space-y-4" data-tour-id="license-validity">
              <div>
                <h3 className="text-sm font-medium text-ink">{t("uxWizard.accessTitle", "When and how can it be used?")}</h3>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="license-kind">{t("licenses.kind", "Kind")}</Label>
                <Select
                  id="license-kind"
                  value={kind}
                  onChange={(event) => {
                    const nextKind = event.target.value as "paid" | "trial";
                    setKind(nextKind);
                    if (nextKind === "trial") {
                      setModel("time_limited");
                      setValidFrom("");
                      setValidUntil("");
                      setPolicy((current) => {
                        try { return JSON.stringify({ ...parseJsonObject(current), max_devices: 1 }, null, 2); }
                        catch { return '{"max_devices":1}'; }
                      });
                    }
                    clearValidityError();
                  }}
                >
                  <option value="paid">{t("licenses.paid", "Paid")}</option>
                  <option value="trial">{t("licenses.trial", "Trial")}</option>
                </Select>
                <p className="text-xs text-muted">
                  {isTrial
                    ? t("licenses.trialKindHelp", "Trial starts at server UTC when issued and uses one Device by default.")
                    : t("licenses.paidKindHelp", "Paid grants use the selected model and validity window.")}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="license-model">{t("licenses.model", "Model")}</Label>
                <Select
                  id="license-model"
                  value={model}
                  disabled={isTrial}
                  onChange={(event) => {
                    const nextModel = event.target.value as NonNullable<CreateLicenseRequest["model"]>;
                    setModel(nextModel);
                    if (
                      (nextModel !== "time_limited" || validUntil) &&
                      (nextModel !== "perpetual" || !validUntil)
                    ) {
                      clearValidityError();
                    }
                  }}
                >
                  <option value="node_locked">Node locked</option>
                  <option value="time_limited">Time limited</option>
                  <option value="perpetual">Perpetual</option>
                </Select>
              </div>
              {isTrial ? (
                <div className="space-y-2">
                  <Label htmlFor="license-trial-duration">{t("licenses.trialDuration", "Trial duration (days)")}</Label>
                  <Input
                    id="license-trial-duration"
                    type="number"
                    min={1}
                    max={30}
                    step={1}
                    value={durationDays}
                    onChange={(event) => { setDurationDays(event.target.value); setTrialDurationInvalid(false); }}
                    aria-invalid={trialDurationInvalid}
                    aria-describedby={trialDurationInvalid ? "license-trial-duration-help license-trial-duration-error" : "license-trial-duration-help"}
                  />
                  <p id="license-trial-duration-help" className="text-xs text-muted">
                    {t("licenses.trialDurationHelp", "1–30 whole days. Expiry is derived from server UTC at issue time.")}
                  </p>
                  {trialDurationInvalid && <p id="license-trial-duration-error" className="text-xs text-danger">{t("licenses.trialDurationValidation", "Trial duration must be a whole number from 1 to 30 days.")}</p>}
                </div>
              ) : (
              <div className="space-y-2">
                <Label htmlFor="license-valid-from">
                  {t("licenses.availableFrom", { tz: timezoneAbbr, defaultValue: `Available for activation from (${timezoneAbbr})` })}
                </Label>
                <DateTimePicker
                  id="license-valid-from"
                  value={validFrom}
                  onValueChange={(nextValue) => {
                    setValidFrom(nextValue);
                    if (validityError === "validity_window") clearValidityError();
                  }}
                  placeholder={t("licenses.availableFromPlaceholder", "Allow activation immediately")}
                />
                <p className="text-xs text-muted">
                  {validFrom
                    ? t("licenses.scheduledAvailabilityHelp", "Product Keys can be issued now; Activation is rejected until this time.")
                    : t("licenses.availableFromHelp", "Leave blank to allow activation immediately after issue.")}
                </p>
              </div>
              )}
              {isTrial ? <GrantSummary durationDays={durationDays} entitlements={trialEvidence.entitlements} maxDevices={trialEvidence.maxDevices} /> : <div className="space-y-2">
                <Label htmlFor="license-valid-until">
                  {t("licenses.expiresAt", { tz: timezoneAbbr, defaultValue: `Expires at (${timezoneAbbr})` })}{" "}
                  <span className="font-normal text-muted">({expiryRequirement})</span>
                </Label>
                <DateTimePicker
                  id="license-valid-until"
                  value={validUntil}
                  onValueChange={(nextValue) => {
                    setValidUntil(nextValue);
                    if (
                      (model !== "time_limited" || nextValue) &&
                      (model !== "perpetual" || !nextValue)
                    ) {
                      clearValidityError();
                    }
                  }}
                  invalid={validUntilInvalid}
                  placeholder={expiryPlaceholder}
                  aria-describedby={validUntilInvalid ? "license-valid-until-error" : undefined}
                />
                {validityError && (
                  <p id="license-valid-until-error" className="text-xs text-danger">
                    {validityErrorMessage(t, validityError)}
                  </p>
                )}
                <p className="text-xs text-muted">
                  {t("licenses.expiryAbsoluteHelp", "Expiry is absolute and does not move when Activation occurs.")}
                </p>
              </div>}
              </div>
              <LicenseCreationNavigation currentStep={2} onBack={() => setWizardStep(1)} onNext={advanceWizard} disabled={mutation.isPending} />
            </div>
          </LicenseCreationStep>
          <LicenseCreationStep id="license-creation-step-3" active={wizardStep === 3}>
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-ink">{t("uxWizard.limitsTitle", "What access and limits apply?")}</h3>
              </div>
              <LicenseConfigurationFields
                product={selectedProduct}
                kind={kind}
                entitlements={entitlements}
                policy={policy}
                validationError={limitsValidationError}
                onEntitlementsChange={(value) => { setEntitlements(value); setLimitsValidationError(null); }}
                onPolicyChange={(value) => { setPolicy(value); setLimitsValidationError(null); }}
              />
              <LicenseCreationNavigation currentStep={3} onBack={() => setWizardStep(2)} onNext={advanceWizard} disabled={mutation.isPending} />
            </div>
          </LicenseCreationStep>
          <LicenseCreationStep id="license-creation-step-4" active={wizardStep === 4}>
          <div className="space-y-4" data-tour-id="license-issue">
            <div>
              <h3 className="text-sm font-medium text-ink">{t("uxWizard.reviewTitle", "Review before issuing")}</h3>
              <p className="mt-1 text-xs leading-5 text-muted">{t("uxWizard.reviewDescription", "Check the commercial grant, then record the approval reason. Issuing creates an Audit Log entry.")}</p>
            </div>
            <ReviewSummary
              alias={alias.trim()}
              customer={selectedCustomer?.name}
              customerId={selectedCustomer?.id}
              product={selectedProduct?.name}
              productId={selectedProduct?.id}
              kind={kind}
              model={isTrial ? "time_limited" : model}
              durationDays={isTrial ? durationDays : undefined}
              validity={reviewValidity({ isTrial, durationDays, model, validFrom, validUntil, timezoneAbbr, formatDateTime, t })}
              evidence={reviewEvidence(kind, entitlements, policy, t)}
            />
            {error && (
              <div className="space-y-2">
                <InlineAlert
                  title={t("licenses.createErrorTitle", "License was not created")}
                  message={error}
                  variant="error"
                />
                <Button
                  id="license-review-return-to-expiry"
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => moveToWizardStep(2, "license-valid-until")}
                >
                  {t("licenses.reviewEditExpiry", "Back to Expires at")}
                </Button>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="license-reason">{t("dialogs.reason", "Reason")}</Label>
              <Input
                id="license-reason"
                value={reason}
                onChange={(event) => { setReason(event.target.value); setFieldErrors((current) => ({ ...current, reason: false })); }}
                placeholder={t("licenses.reasonPlaceholder", "Approved customer entitlement")}
                aria-invalid={fieldErrors.reason}
                aria-required="true"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setWizardStep(3)} disabled={mutation.isPending}>
                {t("common.back", "Back")}
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={mutation.isPending}
              >
                {mutation.isPending ? (
                  <BusyLabel>{t("common.creating", "Creating…")}</BusyLabel>
                ) : (
                  t("licenses.submitIssue", "Issue license")
                )}
              </Button>
            </div>
          </div>
          </LicenseCreationStep>
        </fieldset>
      </form>
    </PageFrame>
  );
}

function effectiveMaxDevices(value: unknown) {
  return value == null ? 1 : value;
}

function formatMaxDevices(value: unknown, t: ReturnType<typeof useTranslation>["t"]) {
  const effective = effectiveMaxDevices(value);
  if (value == null) return t("configurationEditor.defaultMaxDevices", { count: effective, defaultValue: "{{count}} (server default)" });
  return typeof effective === "number" ? String(effective) : "—";
}

function grantEvidence(entitlements: string, policy: string, t: ReturnType<typeof useTranslation>["t"]) {
  try {
    const entitlementKeys = Object.keys(parseJsonObject(entitlements));
    const maxDevices = parseJsonObject(policy).max_devices;
    return { entitlements: entitlementKeys, maxDevices: formatMaxDevices(maxDevices, t) };
  } catch { return { entitlements: [], maxDevices: "—" }; }
}

function ReviewSummary({
  alias,
  customer,
  customerId,
  product,
  productId,
  kind,
  model,
  durationDays,
  validity,
  evidence,
}: {
  alias?: string;
  customer?: string;
  customerId?: string;
  product?: string;
  productId?: string;
  kind: "paid" | "trial";
  model: string;
  durationDays?: string;
  validity: string;
  evidence: ReturnType<typeof reviewEvidence>;
}) {
  const { t } = useTranslation();
  return (
    <section className="rounded-md border border-line bg-panel-raised p-4" aria-label={t("uxWizard.reviewTitle", "Review before issuing")}>
      <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
        {alias ? <ReviewItem label={t("licenses.alias", "Alias")} value={alias} /> : null}
        <ReviewItem label={t("uxWizard.customerSummary", "Customer")} value={displayRecord(customer, customerId)} />
        <ReviewItem label={t("uxWizard.productSummary", "Product")} value={displayRecord(product, productId)} />
        <ReviewItem label={t("licenses.kind", "Kind")} value={kind === "trial" ? t("licenses.trial", "Trial") : t("licenses.paid", "Paid")} />
        <ReviewItem label={t("uxWizard.accessSummary", "Access model")} value={model} mono />
        <ReviewItem label={t("licenses.validity", "Validity")} value={validity} />
        {durationDays ? <ReviewItem label={t("licenses.trialDuration", "Trial duration (days)")} value={t("licenses.trialDurationValue", { duration: durationDays, defaultValue: "{{duration}} days" })} /> : null}
        <ReviewItem label={t("uxWizard.featuresSummary", "Features allowed")} value={evidence.entitlements.join(", ") || "—"} mono />
        <ReviewItem label={t("uxWizard.limitsSummary", "Device limit")} value={evidence.maxDevices} mono />
        <ReviewItem label={t("uxWizard.offlineSummary", "Offline allowance")} value={evidence.offlineDays} />
        <ReviewItem label={t("uxWizard.refreshSummary", "Online refresh")} value={evidence.refreshHours} />
        <ReviewItem label={t("uxWizard.tokenLifetimeSummary", "Token lifetime")} value={evidence.tokenLifetimeDays} />
        <ReviewItem label={t("uxWizard.warningSummary", "Expiry warning")} value={evidence.warningDays} />
        <ReviewItem label={t("uxWizard.expirationSummary", "After expiry")} value={evidence.postExpiration} />
        <ReviewItem label={t("uxWizard.clockRollbackSummary", "Clock rollback tolerance")} value={evidence.clockRollbackMinutes} />
        <ReviewItem label={t("uxWizard.clockWindowSummary", "Clock check window")} value={evidence.clockWindowHours} />
        <ReviewItem label={t("uxWizard.platformsSummary", "Allowed platforms")} value={evidence.allowedPlatforms} />
        <ReviewItem label={t("uxWizard.versionsSummary", "Allowed versions")} value={evidence.allowedVersions} />
        <ReviewItem label={t("uxWizard.keySeatSummary", "Product Key and seat")} value={t("uxWizard.keySeatDetail", "Issue Product Keys after creating this License. One usable Product Key allocates one seat.")} />
      </dl>
    </section>
  );
}

function ReviewItem({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return <div className="min-w-0"><dt className="text-xs text-muted">{label}</dt><dd className={mono ? "mt-0.5 wrap-break-word font-mono text-xs text-ink" : "mt-0.5 wrap-break-word text-ink"}>{value}</dd></div>;
}

const secondsPerDay = 86_400;
// Mirrors domain.DefaultPolicy at the write boundary; the API does not expose policy defaults.
const defaultPaidOfflineGraceSeconds = 30 * secondsPerDay;
const defaultRefreshAfterSeconds = 3_600;
const defaultMaxTokenLifetimeSeconds = 30 * secondsPerDay;
const defaultWarningBeforeExpirySeconds = 7 * secondsPerDay;
const defaultClockRollbackSeconds = 5 * 60;
const defaultClockCheckWindowSeconds = 24 * 3_600;
const defaultPostExpiration = "restricted";

// Mirror domain.Policy.Normalize: an omitted or zero duration receives the pilot default.
function effectivePolicySeconds(value: unknown, fallback: number) {
  return value === 0 || typeof value !== "number" ? fallback : value;
}

function reviewEvidence(
  kind: "paid" | "trial",
  entitlements: string,
  policy: string,
  t: ReturnType<typeof useTranslation>["t"],
) {
  try {
    const policyObject = parseJsonObject(policy);
    const configuredOfflineGraceSeconds = policyObject.offline_grace_seconds;
    const offlineGraceSeconds = kind === "trial"
      ? 0
      : configuredOfflineGraceSeconds === 0 || typeof configuredOfflineGraceSeconds !== "number"
        ? defaultPaidOfflineGraceSeconds
        : configuredOfflineGraceSeconds;
    const refreshAfterSeconds = effectivePolicySeconds(policyObject.refresh_after_seconds, defaultRefreshAfterSeconds);
    const maxTokenLifetimeSeconds = effectivePolicySeconds(policyObject.max_token_lifetime_seconds, defaultMaxTokenLifetimeSeconds);
    const warningBeforeExpirySeconds = effectivePolicySeconds(policyObject.warning_before_expiry_seconds, defaultWarningBeforeExpirySeconds);
    const clockRollbackSeconds = effectivePolicySeconds(policyObject.clock_rollback_seconds, defaultClockRollbackSeconds);
    const clockCheckWindowSeconds = effectivePolicySeconds(policyObject.clock_check_window_seconds, defaultClockCheckWindowSeconds);
    const configuredPostExpiration = policyObject.post_expiration;
    const postExpiration = kind === "trial"
      ? defaultPostExpiration
      : typeof configuredPostExpiration === "string" && configuredPostExpiration
        ? configuredPostExpiration
        : defaultPostExpiration;
    const allowedPlatforms = policyStringList(policyObject.allowed_platforms);
    const allowedVersions = policyStringList(policyObject.allowed_versions);
    return {
      entitlements: Object.entries(parseJsonObject(entitlements))
        .map(([key, entitlement]) => [key, entitlementValue(entitlement)] as const)
        .filter(([, value]) => value !== false)
        .map(([key, value]) => value === true ? key : `${key}: ${formatEntitlementReviewValue(value)}`),
      maxDevices: formatMaxDevices(policyObject.max_devices, t),
      offlineDays: t("uxWizard.durationDays", {
        days: offlineGraceSeconds / secondsPerDay,
        defaultValue: "{{days}} days",
      }),
      refreshHours: t("uxWizard.durationHours", {
        hours: refreshAfterSeconds / 3_600,
        defaultValue: "{{hours}} hours",
      }),
      tokenLifetimeDays: t("uxWizard.durationDays", {
        days: maxTokenLifetimeSeconds / secondsPerDay,
        defaultValue: "{{days}} days",
      }),
      warningDays: t("uxWizard.durationDays", {
        days: warningBeforeExpirySeconds / secondsPerDay,
        defaultValue: "{{days}} days",
      }),
      clockRollbackMinutes: t("uxWizard.durationMinutes", {
        minutes: clockRollbackSeconds / 60,
        defaultValue: "{{minutes}} minutes",
      }),
      clockWindowHours: t("uxWizard.durationHours", {
        hours: clockCheckWindowSeconds / 3_600,
        defaultValue: "{{hours}} hours",
      }),
      postExpiration: t(`licenseConfiguration.postExpiration.${postExpiration}`, postExpiration),
      allowedPlatforms: reviewPolicyStringList(allowedPlatforms, t("uxWizard.allSupportedPlatforms", "All supported platforms")),
      allowedVersions: reviewPolicyStringList(allowedVersions, t("uxWizard.allVersions", "All versions")),
    };
  } catch {
    return {
      entitlements: [],
      maxDevices: "—",
      offlineDays: "—",
      refreshHours: "—",
      tokenLifetimeDays: "—",
      warningDays: "—",
      clockRollbackMinutes: "—",
      clockWindowHours: "—",
      postExpiration: "—",
      allowedPlatforms: "—",
      allowedVersions: "—",
    };
  }
}

function policyStringList(value: unknown) {
  if (value === undefined || value === null) return [];
  return Array.isArray(value) && value.every((item): item is string => typeof item === "string") ? value : null;
}

function reviewPolicyStringList(value: string[] | null, fallback: string) {
  if (value === null) return "—";
  return value.join(", ") || fallback;
}

function entitlementValue(entitlement: unknown) {
  if (
    typeof entitlement === "object" &&
    entitlement !== null &&
    "value" in entitlement
  ) {
    return entitlement.value;
  }
  return entitlement;
}

function formatEntitlementReviewValue(value: unknown) {
  if (typeof value === "string" || typeof value === "number") return String(value);
  return JSON.stringify(value);
}

function reviewValidity({
  isTrial,
  durationDays,
  model,
  validFrom,
  validUntil,
  timezoneAbbr,
  formatDateTime,
  t,
}: {
  isTrial: boolean;
  durationDays: string;
  model: string;
  validFrom: string;
  validUntil: string;
  timezoneAbbr: string;
  formatDateTime: (value: string | null | undefined, fallback?: string) => string;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  if (isTrial) return t("licenses.trialExpiryCalculation", { duration: durationDays || "—", defaultValue: "server UTC issuance + {{duration}} whole days" });
  if (model === "perpetual") return t("licenses.perpetual", "Perpetual license (no expiry)");
  const from = validFrom
    ? formatDateTime(validFrom)
    : t("licenses.availableFromPlaceholder", "Allow activation immediately");
  const until = validUntil ? formatDateTime(validUntil) : t("uxWizard.noExpiry", "No expiry");
  return `${from} → ${until} (${timezoneAbbr})`;
}

function displayRecord(name?: string, id?: string) {
  if (!name && !id) return "—";
  return name && id ? `${name} · ${id}` : name ?? id!;
}

function GrantSummary({ durationDays, entitlements, maxDevices }: { durationDays: string; entitlements: string[]; maxDevices: string }) {
  const { t } = useTranslation();
  return <section className="rounded-md border border-line bg-panel-raised p-3" aria-label={t("licenses.trialGrantSummary", "Trial grant summary")}>
    <p className="text-xs font-semibold text-ink">{t("licenses.trialGrantSummary", "Trial grant summary")}</p>
    <dl className="mt-2 grid gap-1 text-xs sm:grid-cols-2">
      <dt>{t("licenses.kind", "Kind")}</dt><dd>Trial</dd>
      <dt>{t("licenses.model", "Model")}</dt><dd>time_limited</dd>
      <dt>{t("licenses.trialStarts", "Starts")}</dt><dd>server UTC</dd>
      <dt>{t("licenses.trialDurationLabel", "Duration")}</dt><dd>{t("licenses.trialDurationValue", { duration: durationDays || "—", defaultValue: "{{duration}} days" })}</dd>
      <dt>{t("licenses.trialExpiryLabel", "Expiry")}</dt><dd>{t("licenses.trialExpiryCalculation", { duration: durationDays || "—", defaultValue: "server UTC issuance + {{duration}} whole days" })}</dd>
      <dt>max_devices</dt><dd>{maxDevices}</dd>
      <dt>{t("licenses.entitlements", "Entitlements")}</dt><dd>{entitlements.length} · {entitlements.join(", ") || "—"}</dd>
    </dl>
  </section>;
}
